import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import useTenantStore, { selectTenantId } from '../../stores/TenantStore';
import OpenAPI from '../../endpoint/Openapi';
import { type paths } from '../../endpoint/Schema';
import DateTimeUtils from '../../utils/DateTimeUtils';
import dayjs from 'dayjs';
import { formatMileage, noop } from '../../utils/GeneralUtils';
import styles from './TripDetail.module.scss';
import GoogleMapsTrip from '../../components/GoogleMapsTrip';
import { useDemoMode } from '../../contexts/DemoModeContext';
import {
  maskEmail,
  maskName,
  maskAddress,
  maskCoordinate,
} from '../../utils/piiMasking';

function TripDetail() {
  const params = useParams();
  const navigate = useNavigate();
  const tenantId = useTenantStore(selectTenantId);
  const { isDemoModeActive } = useDemoMode();
  const { userId, tripId } = params;

  const [loading, setLoading] = useState(false);
  const [tripData, setTripData] = useState<
    | paths['/api/v2/admin/trips/{userId}']['get']['responses']['200']['content']['application/json']['page'][0]
    | null
  >(null);
  const [waypointsData, setWaypointsData] = useState<
    Array<{
      lat: number;
      long: number;
      speed?: number;
      speedLimit?: number;
    }>
  >([]);

  const toast = React.useRef<Toast>(null);

  useEffect(() => {
    const fetchTripData = async () => {
      if (!userId || !tripId || !tenantId) return;

      setLoading(true);
      try {
        // First, get the specific trip from the user trips list
        const tripsRes = await OpenAPI.GET('/api/v2/admin/trips/{userId}', {
          params: {
            path: { userId },
            query: { tenantId, skip: 0, take: 100 }, // Get enough trips to find the one we need
          },
        });

        const specificTrip = tripsRes.data?.page?.find(
          (trip) => trip.id === tripId,
        );

        if (!specificTrip) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Trip not found',
            life: 3000,
          });
          navigate(-1);
          return;
        }

        setTripData(specificTrip);

        // Get waypoints data using the regular endpoint
        const waypointsRes = await OpenAPI.GET(
          '/api/v2/admin/trips/{userId}/trips/{trackToken}/waypoints',
          {
            params: {
              path: { userId, trackToken: tripId },
              query: { tenantId },
            },
          },
        );

        // The waypoints endpoint returns an array directly
        if (waypointsRes.data) {
          const waypoints = waypointsRes.data as Array<{
            lat: number;
            long: number;
            speed?: number;
            speedLimit?: number;
          }>;
          setWaypointsData(waypoints);
        }
      } catch (error) {
        console.error('Error fetching trip data:', error);
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load trip details',
          life: 3000,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTripData().catch(noop);
  }, [userId, tripId, tenantId, navigate]);

  const getScoreClass = (score?: number | null): string => {
    if (!score) return 'noData';
    if (score >= 90) return 'excellent';
    if (score >= 75) return 'good';
    if (score >= 60) return 'fair';
    return 'poor';
  };

  const renderScoreBadge = (score?: number | null) => {
    const roundedScore = score ? Math.round(score) : null;
    return (
      <span className={`${styles.scoreBadge} ${styles[getScoreClass(score)]}`}>
        {roundedScore ?? '-'}
      </span>
    );
  };

  const renderTripInfo = () => {
    if (!tripData) return null;

    const displayUser = isDemoModeActive
      ? tripData.userName
        ? maskName(tripData.userName)
        : tripData.userEmail
          ? maskEmail(tripData.userEmail)
          : 'Unknown'
      : (tripData.userName ?? tripData.userEmail ?? 'Unknown');

    const displayStartPoint = isDemoModeActive
      ? maskAddress(tripData.startPoint)
      : (tripData.startPoint ?? 'Unknown');

    const displayEndPoint = isDemoModeActive
      ? maskAddress(tripData.endPoint)
      : (tripData.endPoint ?? 'Unknown');

    return (
      <Card title="Trip Information" className={styles.infoCard}>
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>User:</span>
            <span className={styles.infoValue}>{displayUser}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Duration:</span>
            <span className={styles.infoValue}>
              {tripData.duration
                ? dayjs
                    .duration(tripData.duration, 'minutes')
                    .format('HH:mm:ss')
                : '-'}
            </span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Distance:</span>
            <span className={styles.infoValue}>
              {formatMileage(tripData.mileage)}
            </span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Start Time:</span>
            <span className={styles.infoValue}>
              {DateTimeUtils.formatDateTime(tripData.startDate)}
            </span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>End Time:</span>
            <span className={styles.infoValue}>
              {DateTimeUtils.formatDateTime(tripData.endDate)}
            </span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Start Point:</span>
            <span className={styles.infoValue}>{displayStartPoint}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>End Point:</span>
            <span className={styles.infoValue}>{displayEndPoint}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Transport Type:</span>
            <span className={styles.infoValue}>{tripData.transportType}</span>
          </div>
        </div>
      </Card>
    );
  };

  const renderScores = () => {
    if (!tripData) return null;

    return (
      <Card title="Trip Scores" className={styles.scoresCard}>
        <div className={styles.scoresGrid}>
          <div className={styles.scoreItem}>
            <span className={styles.scoreLabel}>Safety Score:</span>
            {renderScoreBadge(tripData.score)}
          </div>
          <div className={styles.scoreItem}>
            <span className={styles.scoreLabel}>Acceleration:</span>
            {renderScoreBadge(tripData.accelerationScore)}
          </div>
          <div className={styles.scoreItem}>
            <span className={styles.scoreLabel}>Braking:</span>
            {renderScoreBadge(tripData.brakingScore)}
          </div>
          <div className={styles.scoreItem}>
            <span className={styles.scoreLabel}>Cornering:</span>
            {renderScoreBadge(tripData.corneringScore)}
          </div>
          <div className={styles.scoreItem}>
            <span className={styles.scoreLabel}>Speeding:</span>
            {renderScoreBadge(tripData.speedingScore)}
          </div>
          <div className={styles.scoreItem}>
            <span className={styles.scoreLabel}>Phone Usage:</span>
            {renderScoreBadge(tripData.phoneUsageScore)}
          </div>
        </div>
      </Card>
    );
  };

  const renderMap = () => {
    // When demo mode is active, mask coordinates to reduce precision (~10km)
    const displayWaypoints = isDemoModeActive
      ? waypointsData.map((wp) => ({
          ...wp,
          lat: maskCoordinate(wp.lat) ?? wp.lat,
          long: maskCoordinate(wp.long) ?? wp.long,
        }))
      : waypointsData;

    return (
      <Card title="Trip Route" className={styles.mapCard}>
        <GoogleMapsTrip waypoints={displayWaypoints} />
      </Card>
    );
  };

  return (
    <div className={styles.container}>
      <Toast ref={toast} />

      <div className={styles.header}>
        <Button
          icon="pi pi-arrow-left"
          label="Back"
          className="p-button-text"
          onClick={() => {
            navigate(-1);
          }}
        />
        <h1>Trip Details</h1>
      </div>

      {loading ? (
        <div className={styles.loading}>
          <ProgressSpinner />
          <p>Loading trip details...</p>
        </div>
      ) : (
        <div className={styles.content}>
          <div className={styles.leftColumn}>
            {renderTripInfo()}
            {renderScores()}
          </div>
          <div className={styles.rightColumn}>{renderMap()}</div>
        </div>
      )}
    </div>
  );
}

export default TripDetail;
